import { t } from "@/lib/i18n/interpolate";
import {
  type BudgetLabels,
  budgetTable,
  type Format,
  targetLine,
  wordTargets,
  wordTotal,
} from "@/lib/tool-kit/business-templates/budget";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { splitLines } from "@/lib/tool-kit/essay-templates/text";

/**
 * A business-plan outline in one of two shapes, each with a share of the word count per
 * section and prompts to answer in it.
 *
 * `traditional` follows the sections the US Small Business Administration lists for a
 * traditional plan: executive summary, company description, market analysis,
 * organization and management, service or product line, marketing and sales, funding
 * request, financial projections. `competition` is a pitch-shaped plan for a class or
 * business-plan competition: problem, solution, market, business model, go-to-market,
 * team, financials and the ask. The shares are a planning convention, not a standard.
 */

export const PLAN_FORMATS = ["traditional", "competition"] as const;
export type PlanFormat = (typeof PLAN_FORMATS)[number];

export const SECTIONS: Record<PlanFormat, readonly string[]> = {
  traditional: [
    "summary",
    "company",
    "market",
    "organization",
    "product",
    "marketing",
    "funding",
    "financials",
  ],
  competition: [
    "summary",
    "problem",
    "solution",
    "market",
    "model",
    "gotomarket",
    "team",
    "financials",
    "ask",
  ],
};

/** Percent of the word count per section. Each format sums to 100. */
export const SHARES: Record<PlanFormat, Record<string, number>> = {
  traditional: {
    summary: 5,
    company: 10,
    market: 22,
    organization: 10,
    product: 14,
    marketing: 18,
    funding: 6,
    financials: 15,
  },
  competition: {
    summary: 8,
    problem: 10,
    solution: 12,
    market: 16,
    model: 12,
    gotomarket: 12,
    team: 10,
    financials: 14,
    ask: 6,
  },
};

export const MIN_PLAN_WORDS = 300;
export const MAX_PLAN_WORDS = 50_000;

export interface BusinessPlanInput {
  name: string;
  /** What the business sells and to whom, in the writer's words. */
  offer: string;
  /** Who the customers are and why they would buy. */
  customers: string;
  /** What funding or support is being asked for. */
  ask: string;
  words: string;
  format: PlanFormat;
}

/** Which of the writer's three answers goes into which section. */
const FILLED_BY: Record<string, "offer" | "customers" | "ask"> = {
  company: "offer",
  solution: "offer",
  market: "customers",
  funding: "ask",
  ask: "ask",
};

export interface BusinessPlanLabels {
  defaultTitle: string;
  titleWithName: string;
  subtitles: Record<PlanFormat, string>;
  subtitleWords: string;
  budgetHeading: string;
  budgetNote: string;
  budget: BudgetLabels;
  sectionNames: Record<string, string>;
  tips: Record<string, string>;
  prompts: Record<string, readonly string[]>;
  target: string;
  targetShare: string;
  appendixName: string;
  appendixTip: string;
  checklistHeading: string;
  checklist: readonly string[];
}

export function planWordTotal(raw: string): number | null {
  return wordTotal(raw, MIN_PLAN_WORDS, MAX_PLAN_WORDS);
}

function sectionBlocks(
  key: string,
  input: BusinessPlanInput,
  labels: BusinessPlanLabels,
  target: number | null,
  fmt: Format
): DocBlock[] {
  const share = SHARES[input.format][key];
  const field = FILLED_BY[key];
  const written = field ? splitLines(input[field]) : [];
  const blocks: DocBlock[] = [
    {
      kind: "guidance",
      text: `${targetLine(share, target, { withWords: labels.target, shareOnly: labels.targetShare }, fmt)} ${labels.tips[key]}`,
    },
    ...written.map((text): DocBlock => ({ kind: "paragraph", text })),
  ];
  blocks.push({
    kind: "list",
    list: {
      style: "bullet",
      items: labels.prompts[key].map((text) => ({ text, prompt: true })),
    },
  });
  return blocks;
}

export function buildBusinessPlan(
  input: BusinessPlanInput,
  labels: BusinessPlanLabels,
  fmt: Format
): DocumentModel {
  const keys = SECTIONS[input.format];
  const total = planWordTotal(input.words);
  const targets = wordTargets(keys, SHARES[input.format], total);
  const name = input.name.trim();
  const sections: DocSection[] = keys.map((key) => ({
    heading: labels.sectionNames[key],
    blocks: sectionBlocks(key, input, labels, targets?.[key] ?? null, fmt),
  }));
  const appendix: DocSection[] =
    input.format === "traditional"
      ? [
          {
            heading: labels.appendixName,
            blocks: [{ kind: "guidance", text: labels.appendixTip }],
          },
        ]
      : [];
  return {
    title: name ? t(labels.titleWithName, { name }) : labels.defaultTitle,
    subtitle:
      total === null
        ? labels.subtitles[input.format]
        : `${labels.subtitles[input.format]} · ${t(labels.subtitleWords, { words: fmt(total) })}`,
    sections: [
      {
        heading: labels.budgetHeading,
        blocks: [
          { kind: "guidance", text: labels.budgetNote },
          budgetTable(
            keys,
            labels.sectionNames,
            SHARES[input.format],
            targets,
            labels.budget,
            fmt
          ),
        ],
      },
      ...sections,
      ...appendix,
      {
        heading: labels.checklistHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checklist.map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
